import {
  FormControl,
  Button,
  Box,
  Card,
  CardContent,
  Typography,
  useMediaQuery,
  Alert,
  AlertTitle,
  ButtonGroup,
} from "@mui/material";
import { FormEvent, useEffect, useState } from "react";
import { useCharactersContext } from "../hooks/Context";

interface Question {
  updatedBy: string;
  question: string;
  mediaUrl: string;
  difficultyLevel: string;
  answerType: number;
  active: boolean;
  marks: number;
  updatedDate: string;
  subjectId: number;
  questionBankId: number;
  classId: number;
  createdDate: string;
  createdBy: string;
  optionsList: { optionId: number; option: string; correctAnswer: string }[];
  questionPaperId: number;
  uiType: number;
  remarks: string;
}

interface LayoutProps {
  Questions: Question[];
  currentQuestionIndex: number;
  sectionName: string;
}

const Layout17: React.FC<LayoutProps> = ({
  Questions,
  currentQuestionIndex,
  sectionName,
}) => {
  const {
    questionScores,
    setQuestionScores,
    isViewMode,
    examAnswers,
    setExamAnswers,
  } = useCharactersContext();

  const isMobile = useMediaQuery("(max-width: 600px)");
  const isTablet = useMediaQuery("(max-width:820px)");

  const currentQuestion = Questions[currentQuestionIndex];
  const sentences = currentQuestion?.question.split("<br>");
  const questionText1 = sentences[0];
  const questionText2 = sentences[1];
  const options = currentQuestion?.optionsList ?? [];
  const correctOptions = options
    .filter((option) => option.correctAnswer)
    .map((option) => option.option.trim());

  const [selectedOptions, setSelectedOptions] = useState<string[][]>(() => {
    const storageKey = `selectedOptionsLayout17_${currentQuestion.questionPaperId}`;
    const savedOptions = localStorage.getItem(storageKey);
    return savedOptions
      ? JSON.parse(savedOptions)
      : Array(Questions.length).fill([]);
  });

  const handleOptionChange = (option: string) => {
    setSelectedOptions((prevSelectedOptions) => {
      const updatedOptions = [...prevSelectedOptions];
      const currentOptions = updatedOptions[currentQuestionIndex] || [];
      const isMultiSelect = correctOptions.length > 1;

      if (isMultiSelect) {
        if (currentOptions.includes(option)) {
          updatedOptions[currentQuestionIndex] = currentOptions.filter(
            (item) => item !== option
          );
        } else {
          updatedOptions[currentQuestionIndex] = [...currentOptions, option];
        }
      } else {
        if (currentOptions.includes(option)) {
          updatedOptions[currentQuestionIndex] = [];
        } else {
          updatedOptions[currentQuestionIndex] = [option];
        }
      }

      return updatedOptions;
    });
  };

  const [showAlert, setShowAlert] = useState(false);
  const handleAlertClose = () => setShowAlert(false);
  setTimeout(handleAlertClose, 3000);

  const cleanWord = (text: string) => (text || "").trim().replace(/[.,!?;:]+$/, "").toLowerCase();

  const handleFormSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setShowAlert(true);

    const selectedForCurrentQuestion = selectedOptions[currentQuestionIndex].map(opt => cleanWord(opt));
    const isCorrect = correctOptions.length > 0 && correctOptions.every((option) =>
      selectedForCurrentQuestion.includes(cleanWord(option))
    );

    if (isCorrect) {
      console.log("isCorrect", isCorrect);
      const questionIndex = questionScores.findIndex(
        (item) => item.questionBankId === currentQuestion.questionBankId
      );

      if (questionIndex !== -1) {
        // Update the existing score
        const updatedScores = [...questionScores];
        updatedScores[questionIndex] = {
          questionBankId: currentQuestion.questionBankId,
          questionPaperId: currentQuestion.questionPaperId,
          score: currentQuestion.marks,
          layoutNumber: currentQuestion.uiType,
          sectionName: sectionName,
        };
        setQuestionScores(updatedScores);
      } else {
        // Add a new score for the question
        setQuestionScores((prevScores) => [
          ...prevScores,
          {
            questionBankId: currentQuestion.questionBankId,
            questionPaperId: currentQuestion.questionPaperId,
            score: currentQuestion.marks,
            layoutNumber: currentQuestion.uiType,
            sectionName: sectionName,
          },
        ]);
      }
    } else {
      const correctSelections = selectedOptions[currentQuestionIndex].filter(
        (option) => correctOptions.includes(option.trim())
      );

      if (correctSelections.length > 0) {
        setQuestionScores((prevScores) => [
          ...prevScores,
          {
            questionBankId: currentQuestion.questionBankId,
            questionPaperId: currentQuestion.questionPaperId,
            score: 0,
            layoutNumber: currentQuestion.uiType,
            sectionName: sectionName,
          },
        ]);
      } else {
        setQuestionScores((prevScores) => [
          ...prevScores,
          {
            questionBankId: currentQuestion.questionBankId,
            questionPaperId: currentQuestion.questionPaperId,
            score: 0,
            layoutNumber: currentQuestion.uiType,
            sectionName: sectionName,
          },
        ]);
      }
    }

    const updatedExamAnswer = {
      questionBankId: currentQuestion.questionBankId,
      acutalOptionValue: correctOptions.map(opt => opt.trim()).join(","),
      enteredOptionValue: selectedOptions[currentQuestionIndex].map(opt => opt.trim()).join(","),
    };

    // Check if there is already an answer for the current question
    const existingAnswerIndex = examAnswers.findIndex(
      (answer) => answer.questionBankId === currentQuestion.questionBankId
    );

    if (existingAnswerIndex !== -1) {
      // If an answer already exists, update the existing one
      setExamAnswers((prevAnswers) =>
        prevAnswers.map((answer, index) =>
          index === existingAnswerIndex ? { ...updatedExamAnswer } : answer
        )
      );
    } else {
      // If there is no existing answer, add the new answer
      setExamAnswers((prevAnswers) => [...prevAnswers, updatedExamAnswer]);
    }
  };

  useEffect(() => {
    const storageKey = `selectedOptionsLayout17_${currentQuestion.questionPaperId}`;
    localStorage.setItem(storageKey, JSON.stringify(selectedOptions));
  }, [selectedOptions]);

  useEffect(() => {
    // When isViewMode is true, set selectedOptions to the correctOptions
    if (isViewMode) {
      const newSelectedOptions = Array(Questions.length).fill(correctOptions);
      setSelectedOptions(newSelectedOptions);
    }
  }, [currentQuestion]);

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        marginTop:
          isMobile && isViewMode
            ? "17%"
            : isTablet
            ? "20%"
            : isMobile
            ? "10%"
            : "20%",

        marginBottom: "auto",
      }}
    >
      {showAlert && (
        <Alert
          severity="success"
          variant="filled"
          sx={{
            position: "fixed",
            top: "30%",
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 9999,
            padding: "0.5",
          }}
        >
          <AlertTitle sx={{ color: "white" }}>Answer submitted</AlertTitle>
        </Alert>
      )}
      {currentQuestion && (
        <Card
          variant="outlined"
          sx={{
            borderRadius: "12px",
            boxShadow: "0px 2px 6px rgba(0, 0, 0, 0.1)",
            maxWidth: "82vw",
            pointerEvents: isViewMode ? "none" : "auto",
          }}
        >
          <CardContent>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                flexDirection: "column",
                paddingBottom: "0px",
              }}
            >
              <Typography
                sx={{
                  fontWeight: "700",
                  fontSize: isMobile ? "100%" : "110%",
                }}
              >
                {questionText1}
              </Typography>
            </Box>

            <form onSubmit={handleFormSubmit}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  mt: 2,
                }}
              >
                <FormControl>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      flexWrap: "wrap",
                      gap: "8px",
                      ...(isMobile && {
                        gap: "0px",
                      }),
                    }}
                  >
                    {currentQuestion.answerType === 1 &&
                      options.map((option, index) => (
                        <Button
                          key={option.optionId}
                          onClick={() => handleOptionChange(option.option)}
                          variant={
                            selectedOptions[currentQuestionIndex].includes(
                              option.option
                            )
                              ? "contained"
                              : "outlined"
                          }
                          sx={{
                            m: 1,
                            backgroundColor: selectedOptions[
                              currentQuestionIndex
                            ].includes(option.option)
                              ? "#E12027"
                              : undefined,
                            "&:hover": {
                              backgroundColor: selectedOptions[
                                currentQuestionIndex
                              ].includes(option.option)
                                ? "#E12027"
                                : undefined,
                            },
                            ...(isMobile && {
                              padding: "0.1vh 0.5vh",
                              minWidth: "1vh",
                              minHeight: "1vh",
                            }),
                          }}
                        >
                          {option.option}
                        </Button>
                      ))}
                    {currentQuestion.answerType === 2 && (
                      <ButtonGroup
                        variant="outlined"
                        aria-label="text button group"
                        sx={{
                          display: "flex",
                          overflowX: "auto",
                          flexWrap: "nowrap",
                          pointerEvents: isViewMode ? "auto" : "inherit",
                        }}
                      >
                        {questionText2.split(" ").filter(word => word.trim() !== "").map((word, index) => {
                          // Replace underscores with spaces so multi-word options
                          const displayWord = word.trim().replace(/_/g, " ");
                          const isSelected = selectedOptions[currentQuestionIndex].some(
                            opt => cleanWord(opt) === cleanWord(displayWord)
                          );
                          return (
                          <Button
                            key={index}
                            onClick={() => handleOptionChange(displayWord)}
                            variant={isSelected ? "contained" : "outlined"}
                            sx={{
                              textTransform: "none",
                              padding: displayWord.length < 3 ? 0.1 : 0.5,
                              minWidth:
                                displayWord.length < 3
                                  ? "20px !important"
                                  : "fit-content",
                              backgroundColor: isSelected ? "#E12027" : undefined,
                              "&:hover": {
                                backgroundColor: isSelected ? "#E12027" : undefined,
                              },
                              ...(isMobile && {
                                minWidth:
                                  displayWord.length < 3
                                    ? "5vw !important"
                                    : "fit-content",
                                whiteSpace: "nowrap",
                                flexShrink: 0,
                                padding: displayWord.length < 3 ? 0.2 : 0.5,
                                backgroundColor: isSelected ? "#E12027" : undefined,
                              }),
                              "&:not(:first-of-type):not(:last-of-type)": {
                                borderLeft: "none",
                                borderRight: "none",
                              },
                              "&:last-of-type": {
                                borderLeft: "none",
                              },
                              pointerEvents: isViewMode ? "none" : "auto",
                            }}
                          >
                            {displayWord}
                          </Button>
                        );
                        })}
                      </ButtonGroup>
                    )}
                  </Box>
                </FormControl>
              </Box>
              {!isViewMode && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    mt: 2,
                  }}
                >
                  <Button type="submit" variant="contained">
                    Submit
                  </Button>
                </Box>
              )}
            </form>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
export default Layout17;
